import { useState, useEffect, useRef } from 'react'
import { adminApi, publicApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency, formatDate } from '../../config'
import type { SponsorshipSubmission, SponsorshipStats, Event } from '../../types'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { formatPhoneNumber, lowercaseEmail, isValidEmail, isValidPhone } from '../../utils/formatters'

const PROGRAM_NAMES: Record<string, string> = {
  none: 'None',
  coupon: 'Branded Coupon',
  half: '1/2 Page',
  full: 'Full Page',
}

export default function AdminSponsors() {
  const { getToken } = useAuth()
  const [sponsorships, setSponsorships] = useState<SponsorshipSubmission[]>([])
  const [stats, setStats] = useState<SponsorshipStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSponsorship, setSelectedSponsorship] = useState<SponsorshipSubmission | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [missingFilter, setMissingFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [adminNotes, setAdminNotes] = useState('')
  const [pickupStatus, setPickupStatus] = useState<string>('')
  const [pickupNote, setPickupNote] = useState('')
  const [savingPickup, setSavingPickup] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [downloadingLogo, setDownloadingLogo] = useState(false)
  const logoFileInputRef = useRef<HTMLInputElement>(null)
  const [exporting, setExporting] = useState(false)
  // Foursome golfer editing
  const [foursomeGolfers, setFoursomeGolfers] = useState<Array<{ name: string; email: string; phone: string }>>([])
  const [savingFoursome, setSavingFoursome] = useState(false)
  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)

  // Bulk email state
  const [bulkSendModalOpen, setBulkSendModalOpen] = useState(false)
  const [bulkSendProgress, setBulkSendProgress] = useState({ current: 0, total: 0, errors: 0 })
  const [bulkSending, setBulkSending] = useState(false)
  const bulkSendCancelledRef = useRef(false)

  // Manual sponsorship modal state
  const [manualModalOpen, setManualModalOpen] = useState(false)
  const [events, setEvents] = useState<Event[]>([])
  const [submittingManual, setSubmittingManual] = useState(false)
  const [manualFormData, setManualFormData] = useState({
    eventSlug: '',
    sponsor: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      businessName: '',
      displayName: '',
    },
    holeSelected: false,
    holeAmountCents: 0,
    holePrizeDescription: '',
    holePrizeEstimatedAmountCents: 0,
    holeTentRequested: false,
    tableRunnerAccepted: false,
    silentSponsorshipSelected: false,
    silentAuctionSelected: false,
    silentAuctionPrizeDescription: '',
    silentAuctionPrizeAmountCents: 0,
    silentAuctionPrizePickupInfo: '',
    programOption: 'none' as 'none' | 'coupon' | 'half' | 'full',
    golfFoursomeSelected: false,
    golfFoursomeGolfers: [
      { name: '', email: '', phone: '' },
      { name: '', email: '', phone: '' },
      { name: '', email: '', phone: '' },
      { name: '', email: '', phone: '' },
    ],
    amountPaidCents: 0,
    adminNotes: '',
    sendReceiptEmail: false,
    sendGolferEmails: true,
  })
  const manualLogoFileRef = useRef<HTMLInputElement>(null)
  const [manualLogoKey, setManualLogoKey] = useState<string | null>(null)
  const [manualLogoFileName, setManualLogoFileName] = useState<string | null>(null)
  const [manualLogoUploading, setManualLogoUploading] = useState(false)

  const fetchData = async () => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const [sponsorshipsData, statsData, eventsData] = await Promise.all([
        adminApi.getSponsorships(token),
        adminApi.getSponsorshipStats(token),
        adminApi.getEvents(token),
      ])

      setSponsorships(sponsorshipsData.sponsorships)
      setStats(statsData)
      setEvents(eventsData.events)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [getToken])

  const handleSaveNotes = async () => {
    if (!selectedSponsorship) return

    setSavingNotes(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const updated = await adminApi.updateSponsorship(
        token,
        selectedSponsorship.submissionId,
        { adminNotes }
      )

      // Update in list
      setSponsorships((prev) =>
        prev.map((s) => (s.submissionId === updated.submissionId ? updated : s))
      )
      setSelectedSponsorship(updated)
      alert('Notes saved successfully')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save notes')
    } finally {
      setSavingNotes(false)
    }
  }

  const handleSavePickupStatus = async () => {
    if (!selectedSponsorship) return

    setSavingPickup(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const updated = await adminApi.updateSponsorship(
        token,
        selectedSponsorship.submissionId,
        { pickupStatus, pickupNote }
      )

      setSponsorships((prev) =>
        prev.map((s) => (s.submissionId === updated.submissionId ? updated : s))
      )
      setSelectedSponsorship(updated)
      alert('Pickup status saved')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save pickup status')
    } finally {
      setSavingPickup(false)
    }
  }

  const handleAdminLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedSponsorship) return

    if (file.size > 20 * 1024 * 1024) {
      alert('File size must be under 20MB')
      return
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      alert('File must be PNG, JPG, SVG, or PDF')
      return
    }

    setUploadingLogo(true)
    try {
      const { uploadUrl, logoKey } = await publicApi.getSponsorLogoUploadUrl(file.name, file.type)

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      })

      if (!uploadResponse.ok) throw new Error('Upload failed')

      await publicApi.updateSponsorLogo(selectedSponsorship.submissionId, logoKey)

      // Refresh data
      const updated = { ...selectedSponsorship, silentAuctionLogoUrl: logoKey }
      setSponsorships((prev) =>
        prev.map((s) => (s.submissionId === updated.submissionId ? updated : s))
      )
      setSelectedSponsorship(updated)
      alert('Logo uploaded successfully')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to upload logo')
    } finally {
      setUploadingLogo(false)
      if (logoFileInputRef.current) logoFileInputRef.current.value = ''
    }
  }

  const handleDownloadLogo = async (logoKey: string) => {
    setDownloadingLogo(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const { downloadUrl } = await adminApi.getLogoDownloadUrl(token, logoKey)

      // Open the presigned URL in a new tab
      window.open(downloadUrl, '_blank')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to download logo')
    } finally {
      setDownloadingLogo(false)
    }
  }

  const handleSaveFoursomeGolfers = async () => {
    if (!selectedSponsorship) return

    // Validate golfer fields
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    for (let i = 0; i < foursomeGolfers.length; i++) {
      const g = foursomeGolfers[i]
      if (!g.name) continue
      if (!g.email) {
        alert(`Golfer ${i + 1}: Email is required when a name is entered.`)
        return
      }
      if (!emailRegex.test(g.email)) {
        alert(`Golfer ${i + 1}: Please enter a valid email address.`)
        return
      }
      if (g.phone) {
        const phoneDigits = g.phone.replace(/\D/g, '')
        if (phoneDigits.length !== 10) {
          alert(`Golfer ${i + 1}: Phone number must be 10 digits.`)
          return
        }
      }
    }

    setSavingFoursome(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const updated = await adminApi.updateSponsorship(
        token,
        selectedSponsorship.submissionId,
        { golfFoursomeGolfers: foursomeGolfers.filter(g => g.name || g.email || g.phone) }
      )

      setSponsorships((prev) =>
        prev.map((s) => (s.submissionId === updated.submissionId ? updated : s))
      )
      setSelectedSponsorship(updated)
      alert('Foursome golfer details saved')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save golfer details')
    } finally {
      setSavingFoursome(false)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const blob = await adminApi.exportSponsorships(token)

      // Download the file
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sponsorships-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to export')
    } finally {
      setExporting(false)
    }
  }

  const openEmailModal = (type: 'logo' | 'pickup' | 'custom') => {
    if (!selectedSponsorship) return

    if (type === 'logo') {
      setEmailSubject('Angels on the Green - Logo Upload Request')
      const uploadUrl = `${window.location.origin}/sponsor/upload-logo/${selectedSponsorship.submissionId}`
      setEmailMessage(`Hi Valued Sponsor,\n\nIf you previously uploaded or provided your logo, we sincerely apologize — due to a technical issue, we were unable to save it in our system.\n\nIf you haven't submitted your logo yet, it's game time! We're preparing our print materials now and need your logo ASAP to ensure you're properly recognized at the event.\n\nPlease upload your logo using this secure link:\n${uploadUrl}\n\nAccepted formats: PNG, JPG, SVG, or PDF (max 20MB)\n\nYou may also reply to this email with your logo attached if that's easier.\n\nThank you for your support of School Lunch Angels — we can't wait to showcase our amazing sponsors!`)
    } else if (type === 'pickup') {
      setEmailSubject('Angels on the Green - Prize Pickup Information')
      setEmailMessage(`We need to arrange pickup for your donated prize for our silent auction.\n\nCould you please reply with pickup information? This can be an address or instructions for how we can collect the item.\n\nPlease note: March 31st is the deadline for prize pickups.`)
    } else {
      setEmailSubject('')
      setEmailMessage('')
    }

    setEmailModalOpen(true)
  }

  const handleSendEmail = async () => {
    if (!selectedSponsorship) return

    if (!emailSubject.trim() || !emailMessage.trim()) {
      alert('Subject and message are required')
      return
    }

    setSendingEmail(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      await adminApi.sendSponsorEmail(
        token,
        selectedSponsorship.submissionId,
        emailSubject,
        emailMessage
      )

      alert('Email sent successfully!')
      setEmailModalOpen(false)
      setEmailSubject('')
      setEmailMessage('')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send email')
    } finally {
      setSendingEmail(false)
    }
  }

  // Get sponsors with missing logos
  const getSponsorsWithMissingLogos = () => {
    return sponsorships.filter(s => !s.silentAuctionLogoUrl)
  }

  // Bulk send logo request emails
  const handleBulkSendLogoRequests = async () => {
    const missingLogoSponsors = getSponsorsWithMissingLogos()
    if (missingLogoSponsors.length === 0) return

    setBulkSending(true)
    bulkSendCancelledRef.current = false
    setBulkSendProgress({ current: 0, total: missingLogoSponsors.length, errors: 0 })

    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      let errorCount = 0

      for (let i = 0; i < missingLogoSponsors.length; i++) {
        // Check for cancellation
        if (bulkSendCancelledRef.current) {
          break
        }

        const sponsor = missingLogoSponsors[i]
        const uploadUrl = `${window.location.origin}/sponsor/upload-logo/${sponsor.submissionId}`
        const subject = 'Angels on the Green - Logo Upload Request'
        const message = `Hi Valued Sponsor,\n\nIf you previously uploaded or provided your logo, we sincerely apologize — due to a technical issue, we were unable to save it in our system.\n\nIf you haven't submitted your logo yet, it's game time! We're preparing our print materials now and need your logo ASAP to ensure you're properly recognized at the event.\n\nPlease upload your logo using this secure link:\n${uploadUrl}\n\nAccepted formats: PNG, JPG, SVG, or PDF (max 20MB)\n\nYou may also reply to this email with your logo attached if that's easier.\n\nThank you for your support of School Lunch Angels — we can't wait to showcase our amazing sponsors!`

        try {
          await adminApi.sendSponsorEmail(token, sponsor.submissionId, subject, message)
        } catch {
          errorCount++
        }

        setBulkSendProgress({ current: i + 1, total: missingLogoSponsors.length, errors: errorCount })

        // Throttle: 200ms delay (5 per second, well under SES 12/sec limit)
        if (i < missingLogoSponsors.length - 1 && !bulkSendCancelledRef.current) {
          await new Promise(resolve => setTimeout(resolve, 200))
        }
      }

      if (bulkSendCancelledRef.current) {
        alert(`Bulk send cancelled. Sent ${bulkSendProgress.current} of ${missingLogoSponsors.length} emails.`)
      } else if (errorCount > 0) {
        alert(`Completed with ${errorCount} error(s). ${missingLogoSponsors.length - errorCount} emails sent successfully.`)
      } else {
        alert(`Successfully sent ${missingLogoSponsors.length} logo request emails!`)
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send bulk emails')
    } finally {
      setBulkSending(false)
      setBulkSendModalOpen(false)
    }
  }

  const cancelBulkSend = () => {
    bulkSendCancelledRef.current = true
  }

  const resetManualForm = () => {
    setManualFormData({
      eventSlug: events.length === 1 ? events[0].slug : '',
      sponsor: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        businessName: '',
        displayName: '',
      },
      holeSelected: false,
      holeAmountCents: 0,
      holePrizeDescription: '',
      holePrizeEstimatedAmountCents: 0,
      holeTentRequested: false,
      tableRunnerAccepted: false,
      silentSponsorshipSelected: false,
      silentAuctionSelected: false,
      silentAuctionPrizeDescription: '',
      silentAuctionPrizeAmountCents: 0,
      silentAuctionPrizePickupInfo: '',
      programOption: 'none',
      golfFoursomeSelected: false,
      golfFoursomeGolfers: [
        { name: '', email: '', phone: '' },
        { name: '', email: '', phone: '' },
        { name: '', email: '', phone: '' },
        { name: '', email: '', phone: '' },
      ],
      amountPaidCents: 0,
      adminNotes: '',
      sendReceiptEmail: false,
      sendGolferEmails: true,
    })
    setManualLogoKey(null)
    setManualLogoFileName(null)
  }

  const handleManualLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 20 * 1024 * 1024) {
      alert('File size must be under 20MB')
      return
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/svg+xml']
    if (!allowedTypes.includes(file.type)) {
      alert('File must be PNG, JPG, SVG, or PDF')
      return
    }

    setManualLogoUploading(true)
    try {
      const { uploadUrl, logoKey } = await publicApi.getSponsorLogoUploadUrl(file.name, file.type)

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      })

      if (!uploadResponse.ok) throw new Error('Upload failed')

      setManualLogoKey(logoKey)
      setManualLogoFileName(file.name)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to upload logo')
    } finally {
      setManualLogoUploading(false)
      if (manualLogoFileRef.current) manualLogoFileRef.current.value = ''
    }
  }

  const calculateManualTotal = () => {
    let total = 0
    if (manualFormData.holeSelected) {
      total += manualFormData.holeAmountCents
    }
    if (manualFormData.silentSponsorshipSelected) {
      total += 50000 // $500 fixed
    }
    if (manualFormData.programOption !== 'none' && !manualFormData.holeSelected && !manualFormData.silentSponsorshipSelected) {
      const programPrices = { coupon: 5000, half: 7500, full: 15000 }
      total += programPrices[manualFormData.programOption] || 0
    }
    if (manualFormData.golfFoursomeSelected) {
      total += 60000 // $600
    }
    return total
  }

  const calculateManualInKind = () => {
    let total = 0
    if (manualFormData.holeSelected) {
      total += manualFormData.holePrizeEstimatedAmountCents
    }
    if (manualFormData.silentAuctionSelected) {
      total += manualFormData.silentAuctionPrizeAmountCents
    }
    return total
  }

  const handleSubmitManualSponsorship = async () => {
    // Validation
    if (!manualFormData.eventSlug) {
      alert('Please select an event')
      return
    }
    if (!manualFormData.sponsor.firstName || !manualFormData.sponsor.lastName) {
      alert('Sponsor first and last name are required')
      return
    }
    if (!manualFormData.sponsor.email) {
      alert('Sponsor email is required')
      return
    }
    if (!manualFormData.sponsor.phone) {
      alert('Sponsor phone is required')
      return
    }
    if (!manualFormData.holeSelected && !manualFormData.silentSponsorshipSelected && !manualFormData.silentAuctionSelected && manualFormData.programOption === 'none' && !manualFormData.golfFoursomeSelected) {
      alert('Please select at least one sponsorship option')
      return
    }
    // Silent sponsorship exclusivity validation
    if (manualFormData.silentSponsorshipSelected) {
      if (manualFormData.holeSelected) {
        alert('Silent Sponsorship cannot be combined with Hole Sponsorship')
        return
      }
      if (manualFormData.programOption !== 'none') {
        alert('Silent Sponsorship already includes a full program page')
        return
      }
    }
    if (manualFormData.holeSelected && manualFormData.holeAmountCents < 50000) {
      alert('Hole sponsorship amount must be at least $500')
      return
    }
    // Validate sponsor email and phone format
    if (!isValidEmail(manualFormData.sponsor.email)) {
      alert('Please enter a valid sponsor email address')
      return
    }
    if (!isValidPhone(manualFormData.sponsor.phone)) {
      alert('Please enter a valid 10-digit sponsor phone number')
      return
    }
    // Validate golfer emails and phones if foursome selected
    if (manualFormData.golfFoursomeSelected) {
      for (let i = 0; i < manualFormData.golfFoursomeGolfers.length; i++) {
        const golfer = manualFormData.golfFoursomeGolfers[i]
        if (golfer.email && !isValidEmail(golfer.email)) {
          alert(`Golfer ${i + 1} has an invalid email address`)
          return
        }
        if (golfer.phone && !isValidPhone(golfer.phone)) {
          alert(`Golfer ${i + 1} has an invalid phone number (must be 10 digits)`)
          return
        }
      }
    }

    setSubmittingManual(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      await adminApi.createManualSponsorship(token, {
        eventSlug: manualFormData.eventSlug,
        sponsor: manualFormData.sponsor,
        holeSelected: manualFormData.holeSelected,
        holeAmountCents: manualFormData.holeSelected ? manualFormData.holeAmountCents : undefined,
        holePrizeDescription: manualFormData.holeSelected ? manualFormData.holePrizeDescription : undefined,
        holePrizeEstimatedAmountCents: manualFormData.holeSelected ? manualFormData.holePrizeEstimatedAmountCents : undefined,
        holeTentRequested: manualFormData.holeSelected ? manualFormData.holeTentRequested : undefined,
        tableRunnerAccepted: manualFormData.holeSelected ? manualFormData.tableRunnerAccepted : undefined,
        silentSponsorshipSelected: manualFormData.silentSponsorshipSelected,
        silentAuctionSelected: manualFormData.silentAuctionSelected,
        silentAuctionPrizeDescription: manualFormData.silentAuctionSelected ? manualFormData.silentAuctionPrizeDescription : undefined,
        silentAuctionPrizeAmountCents: manualFormData.silentAuctionSelected ? manualFormData.silentAuctionPrizeAmountCents : undefined,
        silentAuctionPrizePickupInfo: manualFormData.silentAuctionSelected ? manualFormData.silentAuctionPrizePickupInfo : undefined,
        programOption: manualFormData.programOption,
        programOptionSource: manualFormData.programOption !== 'none' ? 'brandedPageSection' : 'none',
        golfFoursomeSelected: manualFormData.golfFoursomeSelected,
        golfFoursomeGolfers: manualFormData.golfFoursomeSelected ? manualFormData.golfFoursomeGolfers.filter(g => g.name || g.email) : undefined,
        amountPaidCents: manualFormData.amountPaidCents || calculateManualTotal(),
        logoKey: manualLogoKey || undefined,
        adminNotes: manualFormData.adminNotes || undefined,
        sendReceiptEmail: manualFormData.sendReceiptEmail,
        sendGolferEmails: manualFormData.golfFoursomeSelected ? manualFormData.sendGolferEmails : undefined,
      })

      alert('Manual sponsorship created successfully!')
      setManualModalOpen(false)
      resetManualForm()
      setSearchQuery('') // Clear search so new record appears
      fetchData() // Refresh the list
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create sponsorship')
    } finally {
      setSubmittingManual(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'no_payment_required':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-600'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid':
        return 'Paid'
      case 'pending':
        return 'Pending'
      case 'no_payment_required':
        return 'No Payment'
      default:
        return status
    }
  }

  const getSponsorDisplayName = (submission: SponsorshipSubmission) => {
    return (
      submission.sponsor.displayName ||
      submission.sponsor.businessName ||
      `${submission.sponsor.firstName} ${submission.sponsor.lastName}`
    )
  }

  // Filter sponsorships
  const filteredSponsorships = sponsorships.filter((s) => {
    // Status filter
    if (statusFilter !== 'all' && s.status !== statusFilter) {
      return false
    }

    // Type filter
    if (typeFilter === 'hole' && !s.holeSelected) return false
    if (typeFilter === 'silent_sponsorship' && !s.silentSponsorshipSelected) return false
    if (typeFilter === 'silent_auction' && !s.silentAuctionSelected) return false
    if (typeFilter === 'program_only' && (s.holeSelected || s.silentSponsorshipSelected || s.silentAuctionSelected)) return false
    if (typeFilter === 'tent' && !s.holeTentRequested) return false
    if (typeFilter === 'tablerunner' && !s.tableRunnerAccepted) return false

    // Missing items filter
    if (missingFilter === 'logo_missing' && s.silentAuctionLogoUrl) return false
    if (missingFilter === 'pickup_missing' && (!s.silentAuctionSelected || s.silentAuctionPrizePickupInfo || s.pickupStatus === 'scheduled' || s.pickupStatus === 'complete')) return false

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      const name = getSponsorDisplayName(s).toLowerCase()
      const email = s.sponsor.email.toLowerCase()
      const business = (s.sponsor.businessName || '').toLowerCase()
      if (!name.includes(query) && !email.includes(query) && !business.includes(query)) {
        return false
      }
    }

    return true
  })

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchData} className="btn btn-primary">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Sponsors</h1>

        <div className="flex gap-2">
          <button
            onClick={() => {
              setManualModalOpen(true)
              // Set default event if only one exists
              if (events.length === 1) {
                setManualFormData(prev => ({ ...prev, eventSlug: events[0].slug }))
              }
            }}
            className="btn btn-primary"
          >
            Add Manual Sponsorship
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="btn btn-secondary"
          >
            {exporting ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Exporting...
              </span>
            ) : (
              'Export CSV'
            )}
          </button>
        </div>
      </div>

      {/* KPI Summary */}
      {stats && (
        <div className="card mb-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
            <div
              className={`cursor-pointer p-2 rounded ${statusFilter === 'paid' ? 'bg-green-50 ring-2 ring-green-500' : 'hover:bg-gray-50'}`}
              onClick={() => setStatusFilter(statusFilter === 'paid' ? 'all' : 'paid')}
            >
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.totalRevenueCents)}
              </p>
              <p className="text-sm text-gray-500">Revenue ({stats.paidCount} paid)</p>
            </div>
            <div className="p-2">
              <p className="text-2xl font-bold text-sla-blue">
                {formatCurrency(stats.totalInKindCents)}
              </p>
              <p className="text-sm text-gray-500">In-Kind Value</p>
            </div>
            <div
              className={`cursor-pointer p-2 rounded ${typeFilter === 'hole' ? 'bg-purple-50 ring-2 ring-purple-500' : 'hover:bg-gray-50'}`}
              onClick={() => setTypeFilter(typeFilter === 'hole' ? 'all' : 'hole')}
            >
              <p className="text-2xl font-bold text-purple-600">
                {stats.holeSponsorCount}
              </p>
              <p className="text-sm text-gray-500">Hole Sponsors</p>
            </div>
            <div
              className={`cursor-pointer p-2 rounded ${typeFilter === 'silent_sponsorship' ? 'bg-green-50 ring-2 ring-green-500' : 'hover:bg-gray-50'}`}
              onClick={() => setTypeFilter(typeFilter === 'silent_sponsorship' ? 'all' : 'silent_sponsorship')}
            >
              <p className="text-2xl font-bold text-green-600">
                {stats.silentSponsorshipCount}
              </p>
              <p className="text-sm text-gray-500">Silent Sponsors</p>
            </div>
            <div
              className={`cursor-pointer p-2 rounded ${typeFilter === 'silent_auction' ? 'bg-orange-50 ring-2 ring-orange-500' : 'hover:bg-gray-50'}`}
              onClick={() => setTypeFilter(typeFilter === 'silent_auction' ? 'all' : 'silent_auction')}
            >
              <p className="text-2xl font-bold text-orange-600">
                {stats.silentAuctionCount}
              </p>
              <p className="text-sm text-gray-500">Silent Auction</p>
            </div>
            <div
              className={`cursor-pointer p-2 rounded ${typeFilter === 'program_only' ? 'bg-blue-50 ring-2 ring-blue-500' : 'hover:bg-gray-50'}`}
              onClick={() => setTypeFilter(typeFilter === 'program_only' ? 'all' : 'program_only')}
            >
              <p className="text-2xl font-bold text-blue-600">
                {stats.programOnlyCount}
              </p>
              <p className="text-sm text-gray-500">Program Only</p>
            </div>
            <div
              className={`cursor-pointer p-2 rounded ${statusFilter === 'pending' ? 'bg-yellow-50 ring-2 ring-yellow-500' : 'hover:bg-gray-50'}`}
              onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
            >
              <p className="text-2xl font-bold text-yellow-600">
                {stats.pendingCount}
              </p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>

          {/* Requested Items Filter */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <span className="text-sm text-gray-600">Requested Items:</span>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setTypeFilter(typeFilter === 'tent' ? 'all' : 'tent')}
                  className={`px-3 py-1 rounded text-sm ${typeFilter === 'tent' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Tent ({stats.tentRequestedCount})
                </button>
                <button
                  onClick={() => setTypeFilter(typeFilter === 'tablerunner' ? 'all' : 'tablerunner')}
                  className={`px-3 py-1 rounded text-sm ${typeFilter === 'tablerunner' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Table Runner ({stats.tableRunnerAcceptedCount})
                </button>
              </div>
            </div>
          </div>

          {/* Outstanding Items Filter */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <span className="text-sm text-gray-600">Outstanding Items:</span>
              <div className="flex gap-2 flex-wrap items-center">
                <button
                  onClick={() => setMissingFilter(missingFilter === 'logo_missing' ? 'all' : 'logo_missing')}
                  className={`px-3 py-1 rounded text-sm ${missingFilter === 'logo_missing' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
                >
                  Logo Missing ({sponsorships.filter(s => !s.silentAuctionLogoUrl).length})
                </button>
                {sponsorships.filter(s => !s.silentAuctionLogoUrl).length > 0 && (
                  <button
                    onClick={() => setBulkSendModalOpen(true)}
                    className="px-3 py-1 rounded text-sm bg-sla-blue text-white hover:bg-blue-700"
                  >
                    Request All ({sponsorships.filter(s => !s.silentAuctionLogoUrl).length})
                  </button>
                )}
                <span className="mx-2 text-gray-300">|</span>
                <button
                  onClick={() => setMissingFilter(missingFilter === 'pickup_missing' ? 'all' : 'pickup_missing')}
                  className={`px-3 py-1 rounded text-sm ${missingFilter === 'pickup_missing' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'}`}
                >
                  Pickup Missing ({sponsorships.filter(s => s.silentAuctionSelected && !s.silentAuctionPrizePickupInfo && s.pickupStatus !== 'scheduled' && s.pickupStatus !== 'complete').length})
                </button>
              </div>
            </div>
          </div>

          {(statusFilter !== 'all' || typeFilter !== 'all' || missingFilter !== 'all') && (
            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setStatusFilter('all')
                  setTypeFilter('all')
                  setMissingFilter('all')
                }}
                className="text-sm text-sla-blue hover:underline"
              >
                Clear all filters - Show all sponsorships
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="card mb-6">
        {/* Hidden field to trap browser autofill */}
        <input type="text" name="search-trap" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
        <input
          type="search"
          className="input"
          placeholder="Search by name, email, or business name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="new-password"
          name="sponsor-search-query"
          data-lpignore="true"
          data-form-type="other"
        />
      </div>

      {/* Sponsorships Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  Sponsor
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">
                  Date
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  Type
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">
                  Amount Due
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">
                  In-Kind
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredSponsorships.map((s) => (
                <tr key={s.submissionId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">
                      {getSponsorDisplayName(s)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {s.sponsor.email}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(s.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1 flex-wrap">
                      {s.holeSelected && (
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                          Hole
                        </span>
                      )}
                      {s.silentSponsorshipSelected && (
                        <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded text-xs">
                          Silent
                        </span>
                      )}
                      {s.silentAuctionSelected && (
                        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">
                          Auction
                        </span>
                      )}
                      {s.programOption !== 'none' && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {PROGRAM_NAMES[s.programOption]}
                        </span>
                      )}
                      {s.golfFoursomeSelected && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                          Foursome
                          {(() => {
                            const assigned = (s.golfFoursomeGolfers || []).filter((g: { name?: string }) => g.name).length
                            return ` (${assigned}/4)`
                          })()}
                        </span>
                      )}
                      {s.couponCode && (
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                          Coupon
                        </span>
                      )}
                    </div>
                    {/* Missing info flags */}
                    <div className="flex justify-center gap-1 flex-wrap mt-1">
                      {/* Logo missing flag - show for any sponsorship type */}
                      {!s.silentAuctionLogoUrl && (
                        <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs">
                          Logo Missing
                        </span>
                      )}
                      {/* Pickup status flag - only for silent auction */}
                      {s.silentAuctionSelected && (
                        s.pickupStatus === 'complete' ? (
                          <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                            Pickup Complete
                          </span>
                        ) : s.pickupStatus === 'scheduled' ? (
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                            Pickup Scheduled
                          </span>
                        ) : !s.silentAuctionPrizePickupInfo ? (
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                            Pickup Missing
                          </span>
                        ) : null
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {s.amountDueCents > 0 ? formatCurrency(s.amountDueCents) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {s.inKindTotalCents > 0 ? formatCurrency(s.inKindTotalCents) : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(s.status)}`}
                    >
                      {getStatusLabel(s.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => {
                        setSelectedSponsorship(s)
                        setAdminNotes(s.adminNotes || '')
                        setPickupStatus(s.pickupStatus || 'pending')
                        setPickupNote(s.pickupNote || '')
                        // Initialize foursome golfers (pad to 4 slots)
                        const existing = (s.golfFoursomeGolfers || []) as Array<{ name?: string; email?: string; phone?: string }>
                        const padded = Array.from({ length: 4 }, (_, i) => ({
                          name: existing[i]?.name || '',
                          email: existing[i]?.email || '',
                          phone: existing[i]?.phone || '',
                        }))
                        setFoursomeGolfers(padded)
                      }}
                      className="text-sla-blue hover:underline text-sm"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredSponsorships.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchQuery
                ? 'No sponsorships match your search'
                : statusFilter !== 'all' || typeFilter !== 'all'
                ? 'No sponsorships match the selected filters'
                : 'No sponsorships found'}
            </div>
          )}
        </div>
      </div>

      {/* Sponsorship Detail Modal */}
      {selectedSponsorship && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Hidden file input for admin logo upload */}
            <input
              type="file"
              ref={logoFileInputRef}
              onChange={handleAdminLogoUpload}
              accept="image/*"
              className="hidden"
            />
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Sponsorship Details
                </h2>
                <button
                  onClick={() => setSelectedSponsorship(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex items-center gap-4">
                  <span className="font-medium">Status:</span>
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${getStatusBadgeClass(selectedSponsorship.status)}`}
                  >
                    {getStatusLabel(selectedSponsorship.status)}
                  </span>
                </div>

                {/* Contact Info */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Sponsor Contact</h3>
                  <p className="font-medium">{getSponsorDisplayName(selectedSponsorship)}</p>
                  <p className="text-sm text-gray-600">
                    {selectedSponsorship.sponsor.firstName} {selectedSponsorship.sponsor.lastName}
                  </p>
                  <p className="text-sm text-gray-600">{selectedSponsorship.sponsor.email}</p>
                  <p className="text-sm text-gray-600">{selectedSponsorship.sponsor.phone}</p>
                  {selectedSponsorship.sponsor.businessName && (
                    <p className="text-sm text-gray-600">
                      Business: {selectedSponsorship.sponsor.businessName}
                    </p>
                  )}
                </div>

                {/* Hole Sponsorship */}
                {selectedSponsorship.holeSelected && (
                  <div className="border rounded-lg p-4 bg-purple-50">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Hole Sponsorship - {formatCurrency(selectedSponsorship.holeAmountCents || 0)}
                    </h3>
                    <p className="text-sm mb-2">
                      <strong>Prize:</strong> {selectedSponsorship.holePrizeDescription}
                    </p>
                    <p className="text-sm mb-2">
                      <strong>Estimated Value:</strong>{' '}
                      {formatCurrency(selectedSponsorship.holePrizeEstimatedAmountCents || 0)}
                    </p>
                    {(selectedSponsorship.holeTentRequested ||
                      selectedSponsorship.tableRunnerAccepted) && (
                      <div className="mt-2 pt-2 border-t border-purple-200">
                        <p className="font-medium text-sm mb-1">Requested Items:</p>
                        <div className="flex gap-2 flex-wrap">
                          {selectedSponsorship.holeTentRequested && (
                            <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs">
                              Tent
                            </span>
                          )}
                          {selectedSponsorship.tableRunnerAccepted && (
                            <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs">
                              Table Runner
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Silent Sponsorship */}
                {selectedSponsorship.silentSponsorshipSelected && (
                  <div className="border rounded-lg p-4 bg-teal-50">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Silent Sponsorship - $500
                    </h3>
                    <p className="text-sm text-gray-700">
                      <strong>Benefits:</strong> Branded flag at hole, recognition at awards ceremony, full branded page in event program, social media shout out
                    </p>
                    <p className="text-xs text-gray-500 mt-2 italic">
                      Hole assignment handled by event organizers
                    </p>
                  </div>
                )}

                {/* Logo (for all sponsors except those with only golf foursome) */}
                {!selectedSponsorship.silentAuctionSelected && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <h3 className="font-semibold text-gray-700 mb-2">Logo</h3>
                    {selectedSponsorship.silentAuctionLogoUrl ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                          Uploaded
                        </span>
                        <button
                          onClick={() => handleDownloadLogo(selectedSponsorship.silentAuctionLogoUrl!)}
                          disabled={downloadingLogo}
                          className="text-sla-blue hover:underline text-sm flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          {downloadingLogo ? 'Loading...' : 'Download Logo'}
                        </button>
                        <button
                          onClick={() => logoFileInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="text-sla-blue hover:underline text-sm"
                        >
                          {uploadingLogo ? 'Uploading...' : 'Replace Logo'}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                          Not Uploaded
                        </span>
                        <button
                          onClick={() => openEmailModal('logo')}
                          className="text-sla-blue hover:underline text-sm"
                        >
                          Request Logo
                        </button>
                        <button
                          onClick={() => logoFileInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="text-sla-blue hover:underline text-sm"
                        >
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Silent Auction */}
                {selectedSponsorship.silentAuctionSelected && (
                  <div className="border rounded-lg p-4 bg-orange-50">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Silent Auction Contribution
                    </h3>
                    <p className="text-sm mb-2">
                      <strong>Prize:</strong> {selectedSponsorship.silentAuctionPrizeDescription}
                    </p>
                    <p className="text-sm mb-2">
                      <strong>Value:</strong>{' '}
                      {formatCurrency(selectedSponsorship.silentAuctionPrizeAmountCents || 0)}
                    </p>

                    {/* Logo Status */}
                    <div className="mt-3 pt-3 border-t border-orange-200">
                      <p className="font-medium text-sm mb-2">Logo:</p>
                      {selectedSponsorship.silentAuctionLogoUrl ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                            Uploaded
                          </span>
                          <button
                            onClick={() => handleDownloadLogo(selectedSponsorship.silentAuctionLogoUrl!)}
                            disabled={downloadingLogo}
                            className="text-sla-blue hover:underline text-sm flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            {downloadingLogo ? 'Loading...' : 'Download Logo'}
                          </button>
                          <button
                            onClick={() => logoFileInputRef.current?.click()}
                            disabled={uploadingLogo}
                            className="text-sla-blue hover:underline text-sm"
                          >
                            {uploadingLogo ? 'Uploading...' : 'Replace Logo'}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs">
                            Missing
                          </span>
                          <button
                            onClick={() => openEmailModal('logo')}
                            className="text-sla-blue hover:underline text-sm"
                          >
                            Request Logo
                          </button>
                          <button
                            onClick={() => logoFileInputRef.current?.click()}
                            disabled={uploadingLogo}
                            className="text-sla-blue hover:underline text-sm"
                          >
                            {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Pickup Info Status */}
                    <div className="mt-3 pt-3 border-t border-orange-200">
                      <p className="font-medium text-sm mb-2">Prize Pickup:</p>
                      {selectedSponsorship.silentAuctionPrizePickupInfo ? (
                        <p className="text-sm text-gray-700 mb-2">
                          {selectedSponsorship.silentAuctionPrizePickupInfo}
                        </p>
                      ) : (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                            Info Missing
                          </span>
                          {pickupStatus !== 'complete' && pickupStatus !== 'scheduled' && (
                            <button
                              onClick={() => openEmailModal('pickup')}
                              className="text-sla-blue hover:underline text-sm"
                            >
                              Request Pickup Info
                            </button>
                          )}
                        </div>
                      )}
                      {/* Pickup Status Controls */}
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Status:</label>
                          <select
                            className="text-sm border rounded px-2 py-1"
                            value={pickupStatus}
                            onChange={(e) => setPickupStatus(e.target.value)}
                          >
                            <option value="pending">Pending</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="complete">Complete</option>
                          </select>
                        </div>
                        <div>
                          <input
                            type="text"
                            className="input text-sm"
                            placeholder="Pickup note (optional)"
                            value={pickupNote}
                            onChange={(e) => setPickupNote(e.target.value)}
                          />
                        </div>
                        <button
                          onClick={handleSavePickupStatus}
                          disabled={savingPickup}
                          className="px-3 py-1 bg-sla-blue text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {savingPickup ? 'Saving...' : 'Save Pickup Status'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Program Option */}
                {selectedSponsorship.programOption !== 'none' && (
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Program Advertising
                    </h3>
                    <p className="text-sm">
                      {PROGRAM_NAMES[selectedSponsorship.programOption]}
                      {selectedSponsorship.programOptionSource === 'silentAuctionAddOn' && (
                        <span className="text-gray-500 ml-2">(via Silent Auction add-on)</span>
                      )}
                    </p>
                  </div>
                )}

                {/* Golf Foursome */}
                {selectedSponsorship.golfFoursomeSelected && (
                  <div className="border rounded-lg p-4 bg-green-50">
                    <h3 className="font-semibold text-gray-700 mb-2">
                      Golf Foursome Registration - {formatCurrency(selectedSponsorship.golfFoursomeAmountCents || 60000)}
                    </h3>
                    {selectedSponsorship.couponCode && selectedSponsorship.couponDiscountCents && selectedSponsorship.couponDiscountCents > 0 && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                        <p className="text-sm text-purple-700 font-medium">
                          Coupon Applied: <span className="font-mono">{selectedSponsorship.couponCode}</span> (-{formatCurrency(selectedSponsorship.couponDiscountCents)})
                        </p>
                      </div>
                    )}
                    <div className="space-y-3">
                      {foursomeGolfers.map((golfer, index) => (
                        <div key={index} className="border border-green-200 rounded p-3 bg-white">
                          <p className="text-sm font-medium text-gray-600 mb-2">Golfer {index + 1}</p>
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="text"
                              className="input text-sm"
                              placeholder="Name"
                              value={golfer.name}
                              onChange={(e) => {
                                const updated = [...foursomeGolfers]
                                updated[index] = { ...updated[index], name: e.target.value }
                                setFoursomeGolfers(updated)
                              }}
                            />
                            <input
                              type="email"
                              className={`input text-sm ${golfer.name && (!golfer.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(golfer.email)) ? 'border-red-400' : ''}`}
                              placeholder={golfer.name ? 'Email (required)' : 'Email'}
                              value={golfer.email}
                              onChange={(e) => {
                                const updated = [...foursomeGolfers]
                                updated[index] = { ...updated[index], email: e.target.value }
                                setFoursomeGolfers(updated)
                              }}
                              onBlur={(e) => {
                                if (e.target.value) {
                                  const updated = [...foursomeGolfers]
                                  updated[index] = { ...updated[index], email: lowercaseEmail(e.target.value) }
                                  setFoursomeGolfers(updated)
                                }
                              }}
                            />
                            <input
                              type="tel"
                              className={`input text-sm ${golfer.phone && golfer.phone.replace(/\D/g, '').length !== 10 ? 'border-red-400' : ''}`}
                              placeholder="Phone"
                              value={golfer.phone}
                              onChange={(e) => {
                                const updated = [...foursomeGolfers]
                                updated[index] = { ...updated[index], phone: formatPhoneNumber(e.target.value) }
                                setFoursomeGolfers(updated)
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={handleSaveFoursomeGolfers}
                      disabled={savingFoursome}
                      className="btn btn-primary mt-3"
                    >
                      {savingFoursome ? (
                        <span className="flex items-center gap-2">
                          <LoadingSpinner size="sm" />
                          Saving...
                        </span>
                      ) : (
                        'Save Golfer Details'
                      )}
                    </button>
                  </div>
                )}

                {/* Referral Source */}
                {selectedSponsorship.referralSource && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-2">Marketing Attribution</h3>
                    <p className="text-gray-600">
                      <strong>How they heard about us:</strong>{' '}
                      {selectedSponsorship.referralSource === 'Other' && selectedSponsorship.referralSourceOther
                        ? `Other - ${selectedSponsorship.referralSourceOther}`
                        : selectedSponsorship.referralSource}
                    </p>
                  </div>
                )}

                {/* Admin Notes */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Admin Notes</h3>
                  <textarea
                    className="input"
                    rows={3}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this sponsorship..."
                  />
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="btn btn-primary mt-2"
                  >
                    {savingNotes ? (
                      <span className="flex items-center gap-2">
                        <LoadingSpinner size="sm" />
                        Saving...
                      </span>
                    ) : (
                      'Save Notes'
                    )}
                  </button>
                </div>

                {/* Metadata */}
                <div className="text-xs text-gray-500">
                  <p>ID: {selectedSponsorship.submissionId}</p>
                  <p>Created: {formatDate(selectedSponsorship.createdAt)}</p>
                  <p>Updated: {formatDate(selectedSponsorship.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModalOpen && selectedSponsorship && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">Send Email to Sponsor</h2>
                <button
                  onClick={() => {
                    setEmailModalOpen(false)
                    setEmailSubject('')
                    setEmailMessage('')
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* To (read-only) */}
                <div>
                  <label className="label">To</label>
                  <input
                    type="text"
                    className="input bg-gray-50"
                    value={selectedSponsorship.sponsor.email}
                    disabled
                  />
                </div>

                {/* Subject (editable) */}
                <div>
                  <label className="label">Subject *</label>
                  <input
                    type="text"
                    className="input"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject"
                  />
                </div>

                {/* Message (editable) */}
                <div>
                  <label className="label">Message *</label>
                  <textarea
                    className="input"
                    rows={6}
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Enter your message"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setEmailModalOpen(false)
                      setEmailSubject('')
                      setEmailMessage('')
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
                    className="btn btn-primary"
                  >
                    {sendingEmail ? (
                      <span className="flex items-center gap-2">
                        <LoadingSpinner size="sm" />
                        Sending...
                      </span>
                    ) : (
                      'Send Email'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Sponsorship Modal */}
      {manualModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Hidden file input for logo upload */}
            <input
              type="file"
              ref={manualLogoFileRef}
              onChange={handleManualLogoUpload}
              accept="image/*,.pdf,.svg"
              className="hidden"
            />
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-800">Add Manual Sponsorship</h2>
                <button
                  onClick={() => {
                    setManualModalOpen(false)
                    resetManualForm()
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Event Selection */}
                <div>
                  <label className="label">Event *</label>
                  <select
                    className="input"
                    value={manualFormData.eventSlug}
                    onChange={(e) => setManualFormData(prev => ({ ...prev, eventSlug: e.target.value }))}
                  >
                    <option value="">Select an event...</option>
                    {events.map(event => (
                      <option key={event.eventId} value={event.slug}>{event.name}</option>
                    ))}
                  </select>
                </div>

                {/* Sponsor Contact Info */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-3">Sponsor Contact Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">First Name *</label>
                      <input
                        type="text"
                        className="input"
                        value={manualFormData.sponsor.firstName}
                        onChange={(e) => setManualFormData(prev => ({
                          ...prev,
                          sponsor: { ...prev.sponsor, firstName: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="label">Last Name *</label>
                      <input
                        type="text"
                        className="input"
                        value={manualFormData.sponsor.lastName}
                        onChange={(e) => setManualFormData(prev => ({
                          ...prev,
                          sponsor: { ...prev.sponsor, lastName: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="label">Email *</label>
                      <input
                        type="email"
                        className="input"
                        value={manualFormData.sponsor.email}
                        onChange={(e) => setManualFormData(prev => ({
                          ...prev,
                          sponsor: { ...prev.sponsor, email: e.target.value }
                        }))}
                        onBlur={(e) => setManualFormData(prev => ({
                          ...prev,
                          sponsor: { ...prev.sponsor, email: lowercaseEmail(e.target.value) }
                        }))}
                        autoComplete="new-password"
                        name="manual-sponsor-email"
                      />
                    </div>
                    <div>
                      <label className="label">Phone *</label>
                      <input
                        type="tel"
                        className="input"
                        value={manualFormData.sponsor.phone}
                        onChange={(e) => setManualFormData(prev => ({
                          ...prev,
                          sponsor: { ...prev.sponsor, phone: formatPhoneNumber(e.target.value) }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="label">Business Name</label>
                      <input
                        type="text"
                        className="input"
                        value={manualFormData.sponsor.businessName}
                        onChange={(e) => setManualFormData(prev => ({
                          ...prev,
                          sponsor: { ...prev.sponsor, businessName: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="label">Display Name</label>
                      <input
                        type="text"
                        className="input"
                        placeholder={manualFormData.sponsor.businessName || `${manualFormData.sponsor.firstName} ${manualFormData.sponsor.lastName}`.trim()}
                        value={manualFormData.sponsor.displayName}
                        onChange={(e) => setManualFormData(prev => ({
                          ...prev,
                          sponsor: { ...prev.sponsor, displayName: e.target.value }
                        }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Silent Sponsorship */}
                <div className="border rounded-lg p-4">
                  <label className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded border-gray-300 text-sla-green focus:ring-sla-green"
                      checked={manualFormData.silentSponsorshipSelected}
                      onChange={(e) => setManualFormData(prev => ({
                        ...prev,
                        silentSponsorshipSelected: e.target.checked,
                        // Clear incompatible options (hole and program only)
                        holeSelected: e.target.checked ? false : prev.holeSelected,
                        programOption: e.target.checked ? 'none' : prev.programOption,
                      }))}
                      disabled={manualFormData.holeSelected}
                    />
                    <span className="font-semibold text-gray-700">Silent Sponsorship - $500</span>
                  </label>
                  {manualFormData.silentSponsorshipSelected && (
                    <div className="ml-8 p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Includes:</strong> Branded flag at hole, recognition at awards ceremony, full branded page in event program, social media shout out
                      </p>
                      <p className="text-xs text-gray-500 italic">
                        Cannot be combined with Hole Sponsorship or Program Options
                      </p>
                    </div>
                  )}
                </div>

                {/* Hole Sponsorship */}
                <div className={`border rounded-lg p-4 ${manualFormData.silentSponsorshipSelected ? 'opacity-50' : ''}`}>
                  <label className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded border-gray-300 text-sla-blue focus:ring-sla-blue"
                      checked={manualFormData.holeSelected}
                      onChange={(e) => setManualFormData(prev => ({
                        ...prev,
                        holeSelected: e.target.checked,
                        silentSponsorshipSelected: e.target.checked ? false : prev.silentSponsorshipSelected,
                      }))}
                      disabled={manualFormData.silentSponsorshipSelected}
                    />
                    <span className="font-semibold text-gray-700">Hole Sponsorship</span>
                  </label>
                  {manualFormData.holeSelected && (
                    <div className="space-y-4 ml-8">
                      <div>
                        <label className="label">Sponsorship Amount ($) *</label>
                        <input
                          type="number"
                          className="input"
                          min="500"
                          value={manualFormData.holeAmountCents / 100 || ''}
                          onChange={(e) => setManualFormData(prev => ({
                            ...prev,
                            holeAmountCents: Math.round(parseFloat(e.target.value || '0') * 100)
                          }))}
                        />
                        <p className="text-xs text-gray-500 mt-1">Minimum $500</p>
                      </div>
                      <div>
                        <label className="label">Prize Description</label>
                        <textarea
                          className="input"
                          rows={2}
                          value={manualFormData.holePrizeDescription}
                          onChange={(e) => setManualFormData(prev => ({ ...prev, holePrizeDescription: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="label">Prize Estimated Value ($)</label>
                        <input
                          type="number"
                          className="input"
                          value={manualFormData.holePrizeEstimatedAmountCents / 100 || ''}
                          onChange={(e) => setManualFormData(prev => ({
                            ...prev,
                            holePrizeEstimatedAmountCents: Math.round(parseFloat(e.target.value || '0') * 100)
                          }))}
                        />
                      </div>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300"
                            checked={manualFormData.holeTentRequested}
                            onChange={(e) => setManualFormData(prev => ({ ...prev, holeTentRequested: e.target.checked }))}
                          />
                          <span className="text-sm">Tent Requested</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300"
                            checked={manualFormData.tableRunnerAccepted}
                            onChange={(e) => setManualFormData(prev => ({ ...prev, tableRunnerAccepted: e.target.checked }))}
                          />
                          <span className="text-sm">Table Runner</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Silent Auction */}
                <div className="border rounded-lg p-4">
                  <label className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded border-gray-300 text-sla-blue focus:ring-sla-blue"
                      checked={manualFormData.silentAuctionSelected}
                      onChange={(e) => setManualFormData(prev => ({ ...prev, silentAuctionSelected: e.target.checked }))}
                    />
                    <span className="font-semibold text-gray-700">Silent Auction Contribution</span>
                  </label>
                  {manualFormData.silentAuctionSelected && (
                    <div className="space-y-4 ml-8">
                      <div>
                        <label className="label">Prize Description</label>
                        <textarea
                          className="input"
                          rows={2}
                          value={manualFormData.silentAuctionPrizeDescription}
                          onChange={(e) => setManualFormData(prev => ({ ...prev, silentAuctionPrizeDescription: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="label">Prize Value ($)</label>
                        <input
                          type="number"
                          className="input"
                          value={manualFormData.silentAuctionPrizeAmountCents / 100 || ''}
                          onChange={(e) => setManualFormData(prev => ({
                            ...prev,
                            silentAuctionPrizeAmountCents: Math.round(parseFloat(e.target.value || '0') * 100)
                          }))}
                        />
                      </div>
                      <div>
                        <label className="label">Pickup Information</label>
                        <textarea
                          className="input"
                          rows={2}
                          value={manualFormData.silentAuctionPrizePickupInfo}
                          onChange={(e) => setManualFormData(prev => ({ ...prev, silentAuctionPrizePickupInfo: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Program Option */}
                {!manualFormData.holeSelected && !manualFormData.silentSponsorshipSelected && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-700 mb-3">Program Advertising</h3>
                    <div className="space-y-2">
                      {(['none', 'coupon', 'half', 'full'] as const).map((option) => (
                        <label key={option} className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="programOption"
                            className="h-4 w-4 border-gray-300"
                            checked={manualFormData.programOption === option}
                            onChange={() => setManualFormData(prev => ({ ...prev, programOption: option }))}
                          />
                          <span className="text-sm">
                            {option === 'none' && 'None'}
                            {option === 'coupon' && 'Branded Coupon - $50'}
                            {option === 'half' && '1/2 Page - $75'}
                            {option === 'full' && 'Full Page - $150'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Golf Foursome */}
                <div className="border rounded-lg p-4">
                  <label className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded border-gray-300 text-sla-blue focus:ring-sla-blue"
                      checked={manualFormData.golfFoursomeSelected}
                      onChange={(e) => setManualFormData(prev => ({ ...prev, golfFoursomeSelected: e.target.checked }))}
                    />
                    <span className="font-semibold text-gray-700">Golf Foursome - $600</span>
                  </label>
                  {manualFormData.golfFoursomeSelected && (
                    <div className="space-y-3 ml-8">
                      {manualFormData.golfFoursomeGolfers.map((golfer, index) => (
                        <div key={index} className="grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            className="input text-sm"
                            placeholder={`Golfer ${index + 1} name`}
                            value={golfer.name}
                            onChange={(e) => {
                              const updated = [...manualFormData.golfFoursomeGolfers]
                              updated[index] = { ...updated[index], name: e.target.value }
                              setManualFormData(prev => ({ ...prev, golfFoursomeGolfers: updated }))
                            }}
                          />
                          <input
                            type="email"
                            className="input text-sm"
                            placeholder="Email"
                            value={golfer.email}
                            onChange={(e) => {
                              const updated = [...manualFormData.golfFoursomeGolfers]
                              updated[index] = { ...updated[index], email: e.target.value }
                              setManualFormData(prev => ({ ...prev, golfFoursomeGolfers: updated }))
                            }}
                            onBlur={(e) => {
                              if (e.target.value) {
                                const updated = [...manualFormData.golfFoursomeGolfers]
                                updated[index] = { ...updated[index], email: lowercaseEmail(e.target.value) }
                                setManualFormData(prev => ({ ...prev, golfFoursomeGolfers: updated }))
                              }
                            }}
                          />
                          <input
                            type="tel"
                            className="input text-sm"
                            placeholder="Phone"
                            value={golfer.phone}
                            onChange={(e) => {
                              const updated = [...manualFormData.golfFoursomeGolfers]
                              updated[index] = { ...updated[index], phone: formatPhoneNumber(e.target.value) }
                              setManualFormData(prev => ({ ...prev, golfFoursomeGolfers: updated }))
                            }}
                          />
                        </div>
                      ))}
                      {/* Golfer notification checkbox - highlighted */}
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-blue-400 text-blue-600 focus:ring-blue-500"
                            checked={manualFormData.sendGolferEmails}
                            onChange={(e) => setManualFormData(prev => ({ ...prev, sendGolferEmails: e.target.checked }))}
                          />
                          <span className="text-sm font-medium text-blue-800">
                            Send registration notification emails to golfers
                          </span>
                        </label>
                        <p className="text-xs text-blue-600 mt-1 ml-6">
                          Golfers with email addresses will receive an email confirming their tournament registration
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Logo Upload */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-3">Logo</h3>
                  {manualLogoKey ? (
                    <div className="flex items-center gap-3">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                        Uploaded: {manualLogoFileName}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setManualLogoKey(null)
                          setManualLogoFileName(null)
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => manualLogoFileRef.current?.click()}
                      disabled={manualLogoUploading}
                      className="btn btn-secondary"
                    >
                      {manualLogoUploading ? 'Uploading...' : 'Upload Logo'}
                    </button>
                  )}
                </div>

                {/* Payment & Notes */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-3">Payment & Notes</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="label">Amount Paid by Check ($)</label>
                      <input
                        type="number"
                        className="input"
                        placeholder={`Auto-calculated: ${(calculateManualTotal() / 100).toFixed(2)}`}
                        value={manualFormData.amountPaidCents / 100 || ''}
                        onChange={(e) => setManualFormData(prev => ({
                          ...prev,
                          amountPaidCents: Math.round(parseFloat(e.target.value || '0') * 100)
                        }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave blank to use calculated total: {formatCurrency(calculateManualTotal())}
                      </p>
                    </div>
                    <div>
                      <label className="label">Admin Notes</label>
                      <textarea
                        className="input"
                        rows={2}
                        value={manualFormData.adminNotes}
                        onChange={(e) => setManualFormData(prev => ({ ...prev, adminNotes: e.target.value }))}
                      />
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                        checked={manualFormData.sendReceiptEmail}
                        onChange={(e) => setManualFormData(prev => ({ ...prev, sendReceiptEmail: e.target.checked }))}
                      />
                      <span className="text-sm">Send receipt email to sponsor</span>
                    </label>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 mb-2">Summary</h3>
                  <div className="text-sm space-y-1">
                    <p><strong>Total Due:</strong> {formatCurrency(calculateManualTotal())}</p>
                    {calculateManualInKind() > 0 && (
                      <p><strong>In-Kind Value:</strong> {formatCurrency(calculateManualInKind())}</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setManualModalOpen(false)
                      resetManualForm()
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitManualSponsorship}
                    disabled={submittingManual}
                    className="btn btn-primary"
                  >
                    {submittingManual ? (
                      <span className="flex items-center gap-2">
                        <LoadingSpinner size="sm" />
                        Creating...
                      </span>
                    ) : (
                      'Create Sponsorship'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Send Logo Request Modal */}
      {bulkSendModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">
              {bulkSending ? 'Sending Logo Requests...' : 'Confirm Bulk Email'}
            </h2>

            {bulkSending ? (
              <div className="space-y-4">
                <div className="text-center">
                  <LoadingSpinner size="lg" />
                  <p className="mt-4 text-lg font-medium">
                    Sending {bulkSendProgress.current} of {bulkSendProgress.total}...
                  </p>
                  {bulkSendProgress.errors > 0 && (
                    <p className="text-red-600 text-sm mt-2">
                      {bulkSendProgress.errors} error(s) so far
                    </p>
                  )}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-sla-blue h-2 rounded-full transition-all"
                    style={{ width: `${(bulkSendProgress.current / bulkSendProgress.total) * 100}%` }}
                  />
                </div>
                <button
                  onClick={cancelBulkSend}
                  className="btn btn-secondary w-full"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600">
                  This will send logo request emails to <strong>{getSponsorsWithMissingLogos().length}</strong> sponsors
                  who don't have a logo on file.
                </p>
                <p className="text-sm text-gray-500">
                  Emails will be sent at a rate of 5 per second to respect SES limits.
                </p>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setBulkSendModalOpen(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkSendLogoRequests}
                    className="btn btn-primary"
                  >
                    Send {getSponsorsWithMissingLogos().length} Emails
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
